import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../context/AuthContext'
import Layout from '../components/Layout'
import { generateReport } from '../lib/reportGenerator'

export default function Reports() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [trusts, setTrusts] = useState([])
  const [reports, setReports] = useState([])
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [formData, setFormData] = useState({
    trust_id: '',
    report_type: 'annual_accounting',
    date_from: '',
    date_to: new Date().toISOString().split('T')[0]
  })

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)

    const { data: memberData } = await supabase
      .from('trust_members')
      .select('trusts(*)')
      .eq('user_id', user.id)
    setTrusts(memberData?.map(d => d.trusts) || [])

     const { data: reportData } = await supabase
    .from('reports')
   .select('*, trusts(name)')
   .order('created_at', { ascending: false })
   .limit(20)
  setReports(reportData || [])

    setLoading(false)
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleGenerate = async () => {
    setError(null)
    setSuccess(null)

    if (!formData.trust_id) { setError('Please select a trust'); return }
    if (!formData.date_from) { setError('Please select a start date'); return }
    if (!formData.date_to) { setError('Please select an end date'); return }

    setGenerating(true)

    try {
      // Fetch all data needed for the report
      const trust = trusts.find(t => t?.id === formData.trust_id)

      const { data: transactions } = await supabase
        .from('transactions')
        .select('*, accounts(name), categories(name), beneficiaries(full_name)')
        .eq('trust_id', formData.trust_id)
        .gte('date', formData.date_from)
        .lte('date', formData.date_to)
        .order('date', { ascending: true })

      const { data: accounts } = await supabase
        .from('accounts')
        .select('*')
        .eq('trust_id', formData.trust_id)

      const { data: beneficiaries } = await supabase
        .from('beneficiaries')
        .select('*')
        .eq('trust_id', formData.trust_id)

      // Generate the PDF
      generateReport({
        type: formData.report_type,
        trust,
        transactions: transactions || [],
        accounts: accounts || [],
        beneficiaries: beneficiaries || [],
        dateFrom: formData.date_from,
        dateTo: formData.date_to,
        generatedBy: user.email
      })

      // Log the report to the database
      await supabase.from('reports').insert({
        trust_id: formData.trust_id,
        generated_by: user.id,
        report_type: formData.report_type,
        date_range_start: formData.date_from,
        date_range_end: formData.date_to,
        published_at: new Date().toISOString()
      })

      setSuccess('Report generated and downloaded successfully!')
      fetchData()
    } catch (err) {
      setError('Failed to generate report: ' + err.message)
    }

    setGenerating(false)
  }

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  })

  const reportTypeLabels = {
    annual_accounting: 'Annual Accounting',
    disbursement_history: 'Disbursement History',
    transaction_ledger: 'Transaction Ledger',
    principal_income_summary: 'Principal & Income Summary'
  }

  return (
    <Layout>
      <div className="space-y-6">

        {/* Header */}
        <div>
          <h2 className="text-xl font-bold text-gray-900">Reports</h2>
          <p className="text-sm text-gray-500 mt-1">
            Generate and download trust accounting reports as PDF
          </p>
        </div>

        {/* Report Builder */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-800 mb-4">Generate New Report</h3>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm mb-4">
              {success}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">

            {/* Trust */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Trust <span className="text-red-500">*</span>
              </label>
              <select
                name="trust_id"
                value={formData.trust_id}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a trust...</option>
                {trusts.map(t => (
                  <option key={t?.id} value={t?.id}>{t?.name}</option>
                ))}
              </select>
            </div>

            {/* Report Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Report Type <span className="text-red-500">*</span>
              </label>
              <select
                name="report_type"
                value={formData.report_type}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="annual_accounting">Annual Accounting</option>
                <option value="disbursement_history">Disbursement History by Beneficiary</option>
                <option value="transaction_ledger">Transaction Ledger</option>
                <option value="principal_income_summary">Principal & Income Summary</option>
              </select>
            </div>

            {/* Date From */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="date_from"
                value={formData.date_from}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Date To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                To Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="date_to"
                value={formData.date_to}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <button
            onClick={handleGenerate}
            disabled={generating}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2.5 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generating ? 'Generating PDF...' : '⬇ Generate & Download PDF'}
          </button>
        </div>

        {/* Report History */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-800">Report History</h3>
          </div>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <p className="text-gray-400 text-sm">Loading reports...</p>
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-4xl mb-3">📄</p>
              <p className="text-gray-400 text-sm">No reports generated yet</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Trust</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Report Type</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Date Range</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Generated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {reports.map(report => (
                  <tr key={report.id} className="hover:bg-gray-50">
                    <td className="px-6 py-3 font-medium text-gray-900">
                      {report.trusts?.name}
                    </td>
                    <td className="px-6 py-3 text-gray-600">
                      {reportTypeLabels[report.report_type] || report.report_type}
                    </td>
                    <td className="px-6 py-3 text-gray-500">
                      {formatDate(report.date_range_start)} — {formatDate(report.date_range_end)}
                    </td>
                    <td className="px-6 py-3 text-gray-500">
                      {formatDate(report.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  )
}